import { EditIcon, TrashIcon } from "@xola/icons";
import { Badge, FormGroup, Label, Popover, Table } from "@xola/ui-kit";
import dayjs from "dayjs";
import pluralize from "pluralize";
import React from "react";
import {
    Availability,
    CheckInValidation,
    CheckInValidationType,
    CutoffCheckInValidation,
    PurchaseCutoffCheckInValidation,
} from "types";
import { ScheduleRow } from "@/components/Common/ScheduleRow";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { CheckInWindowValidation } from "@/components/Product/ExperienceCheckIn/ExperienceCheckIn.types";
import { humanizeEquipmentDuration } from "@/helpers/durationHelper";

interface ScheduleValidationsTableProps {
    validations: CheckInValidation[];
    onEdit: (validation: CheckInWindowValidation) => void;
    onRemove: (validation: CheckInWindowValidation) => void;
}

export const ScheduleValidationTable = ({ validations, onEdit, onRemove }: ScheduleValidationsTableProps) => {
    return (
        <Table>
            <Table.Head>
                <Table.Row>
                    <Table.Header className="!text-sm">Window Name</Table.Header>
                    <Table.Header className="whitespace-nowrap text-right !text-sm">Actions</Table.Header>
                </Table.Row>
            </Table.Head>

            {validations.length > 0 ? (
                <Table.Body>
                    {validations.map((validation) => {
                        return validation.object === CheckInValidationType.SCHEDULE ? (
                            <ScheduleRow
                                key={validation.id}
                                blackoutSchedules
                                schedule={validation.schedule}
                                setSelectedSchedule={() => onEdit(validation)}
                                onRemove={() => onRemove(validation)}
                            />
                        ) : (
                            <CutoffValidationRow
                                key={validation.id}
                                validation={validation as PurchaseCutoffCheckInValidation}
                                onEdit={onEdit}
                                onRemove={onRemove}
                            />
                        );
                    })}
                </Table.Body>
            ) : (
                <Table.Body>
                    <Table.Row>
                        <Table.Cell colspan={2}>
                            <span>No check-in windows added yet</span>
                        </Table.Cell>
                    </Table.Row>
                </Table.Body>
            )}
        </Table>
    );
};

interface CutoffValidationRowProps {
    validation: PurchaseCutoffCheckInValidation;
    onEdit: (validation: PurchaseCutoffCheckInValidation) => void;
    onRemove: (validation: PurchaseCutoffCheckInValidation) => void;
}

export const CutoffValidationRow = ({ validation, onEdit, onRemove }: CutoffValidationRowProps) => {
    return (
        <Table.Row data-testid="schedule-row">
            <Table.Cell className="max-w-75 truncate">
                <Popover delay={0} distance={10} maxWidth={400} placement="left" skidding={0} trigger="mouseenter">
                    {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- "name" can be "", we need to show fallback text in that scenario */}
                    <u className="text-gray-darker">{validation.name || getCutoffSummary(validation)}</u>
                    <Popover.Content className="space-y-2 p-4">
                        <CutoffSummary validation={validation} />
                    </Popover.Content>
                </Popover>
            </Table.Cell>

            <Table.Cell>
                <div className="flex items-center justify-end space-x-2">
                    <SecondaryButton
                        data-testid="schedule-row-edit-button"
                        size="tiny"
                        onClick={() => onEdit(validation)}
                    >
                        <EditIcon />
                    </SecondaryButton>
                    <SecondaryButton
                        data-testid="schedule-row-delete-button"
                        size="tiny"
                        onClick={() => onRemove(validation)}
                    >
                        <TrashIcon />
                    </SecondaryButton>
                </div>
            </Table.Cell>
        </Table.Row>
    );
};

interface CutoffSummaryProps {
    validation: PurchaseCutoffCheckInValidation;
}

export const CutoffSummary = ({ validation }: CutoffSummaryProps) => {
    return (
        <div>
            <div className="mb-5 flex items-center justify-between">
                <span className="max-w-50 whitespace-normal text-md font-bold">
                    {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- "name" can be "", we need to show fallback text in that scenario */}
                    {validation.name || "Relative to purchase date"}
                </span>
                <Badge color={validation.availability === "available" ? "success" : "danger"} size="medium">
                    <span className="capitalize">{validation.availability}</span>
                </Badge>
            </div>

            <FormGroup>
                <Label>Summary</Label>
                <span className="whitespace-normal">{getCutoffSummary(validation)}</span>
            </FormGroup>
        </div>
    );
};

const getCutoffSummary = (validation: CutoffCheckInValidation) => {
    const { duration, durationUnit } = humanizeEquipmentDuration(dayjs.duration(validation.duration).asMinutes());
    const durationText =
        validation.duration === "P0D" ? "same day" : `${duration} ${pluralize(durationUnit, duration)}`;

    if (validation.availability === Availability.UNAVAILABLE) {
        return `Guests cannot check-in within ${durationText} of purchase date`;
    }

    return `Guests must check-in within ${durationText} of purchase date`;
};
